import { useEffect, useRef } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { api } from '@/api/client'
import { Button } from '@/components/primitives'
import { Notice, useApiErrorMessage } from '@/components/controls'
import { useT } from '@/i18n/context'
import { sessionHasEnded } from '@/lib/sessionActivity'
import type { SessionRow } from '@/terminal/wall'

/**
 * Finishes what a sign-in started.
 *
 * Signing an account in opens a pane and sends the operator to it, because the
 * authorisation code can only be pasted there. Confirming that the credential
 * landed was then left to them: navigate back to Hesaplar, find a button, press
 * it. The hint even said "press Activate *here*" — on a screen the flow had just
 * navigated away from. Skipping it left the account wearing a verdict about the
 * credential it had *replaced*, which is how a working account goes on claiming
 * its session has expired.
 *
 * So the confirmation happens where the operator already is. It spends a real
 * provider request, which is why it is not done by a background sweep: this runs
 * only in an open console, on the pane the operator was sent to, as the tail of
 * the click they made. A closed browser confirms nothing and spends nothing, and
 * the button on Hesaplar is still there for that case.
 *
 * A pane that ended some time ago is not confirmed on its own — arriving at an
 * old sign-in from a link would otherwise spend a request nobody asked for. It
 * offers the button instead.
 */
const signInConfirmWindowMs = 10 * 60_000

export function SignInConfirm({ session }: { session: SessionRow | undefined }) {
  const t = useT()
  const queryClient = useQueryClient()
  const describe = useApiErrorMessage()
  const attempted = useRef<string | null>(null)

  const sessionId = session?.id
  const accountId = session?.accountProfileId
  const isEndedSignIn =
    session?.purpose === 'LOGIN' && !!accountId && sessionHasEnded(session.state)
  const endedRecently =
    !!session?.endedAt && Date.now() - Date.parse(session.endedAt) < signInConfirmWindowMs

  const confirm = useMutation({
    mutationFn: () => api.post<{ state: string }>(`/accounts/${accountId}/probe`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['accounts'] })
      void queryClient.invalidateQueries({ queryKey: ['accounts-matrix'] })
    },
  })
  const mutate = confirm.mutate

  useEffect(() => {
    if (!isEndedSignIn || !endedRecently || !sessionId) return
    // Once per pane, whatever the poll does to this component's renders.
    if (attempted.current === sessionId) return
    attempted.current = sessionId
    mutate()
  }, [isEndedSignIn, endedRecently, sessionId, mutate])

  if (!isEndedSignIn) return null

  const state = confirm.data?.state
  const usable = !!state && ['READY', 'LOW_HEADROOM', 'BUSY'].includes(state)
  /**
   * A non-zero exit says the *attempt* did not complete, and it says why more
   * often than not: `claude auth login` exits 1 when the authorisation code
   * exchange is rejected, which is what happens when the pasted code was not
   * issued for this pane. Every sign-in generates its own PKCE challenge, so a
   * code copied from an earlier pane's link can never work — and an operator who
   * opened the flow twice has two links to confuse.
   *
   * It is shown *beside* the account's verdict rather than instead of it,
   * because the two answer different questions and were both true on
   * 2026-09-22: the attempt failed, and the account came out of it working,
   * since the CLI refreshes an existing credential at startup before any OAuth.
   */
  const attemptFailed = typeof session?.exitStatus === 'number' && session.exitStatus !== 0

  return (
    <div className="k-lo__signin">
      {attemptFailed && <Notice tone="warn">{t('live.signInAttemptFailed')}</Notice>}
      {confirm.isPending ? (
        <Notice tone="info">{t('live.signInConfirming')}</Notice>
      ) : confirm.isError ? (
        <Notice tone="err">{describe(confirm.error).title}</Notice>
      ) : usable ? (
        <Notice tone="ok">{t('live.signInConfirmed')}</Notice>
      ) : (
        <Notice tone="warn">
          {state ? t('live.signInNotReady') : t('live.signInUnconfirmed')}
        </Notice>
      )}
      {!confirm.isPending && !usable && (
        <Button size="sm" variant="primary" onClick={() => mutate()}>
          {t('live.signInConfirmAction')}
        </Button>
      )}
    </div>
  )
}
